import React, { useEffect, useRef } from "react";
import Reveal from "./Reveal";
import SplitText from "./SplitText";
import AppStoreButtons from "./AppStoreButtons";
import useMouseParallax from "../../hooks/useMouseParallax";


const HeroSection = () => {
  const heroVisualRef = useMouseParallax({ strength: 0.35 });
  const sectionRef = useRef(null);

  // Cursor-following spotlight — writes --cursor-x / --cursor-y on the
  // hero section in percent, which the CSS radial-gradient reads.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const prefersReduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    let ticking = false;
    let last = null;
    const tick = () => {
      ticking = false;
      if (!last) return;
      const rect = el.getBoundingClientRect();
      const x = ((last.clientX - rect.left) / rect.width) * 100;
      const y = ((last.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty("--cursor-x", `${x.toFixed(2)}%`);
      el.style.setProperty("--cursor-y", `${y.toFixed(2)}%`);
    };
    const onMove = (e) => {
      last = e;
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(tick);
      }
    };
    el.addEventListener("mousemove", onMove, { passive: true });
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <section className="hero-section" id="hero" ref={sectionRef}>
      <div className="container hero-grid">
        <div className="hero-content">
          <Reveal variant="slide-left">
            <div className="hero-badge">
              <span className="badge-dot"></span>
              <span>Now on iPhone and Android</span>
            </div>
          </Reveal>

          <SplitText
            as="h1"
            className="hero-headline"
            stagger={55}
            baseDelay={120}
          >
            A training plan.<br />A coach in your corner.
          </SplitText>

          <Reveal variant="slide-left" delay={160}>
            <p className="hero-subheadline">
              Personalised workouts, exercise guidance and progress tracking.
              Train with an AI coach that knows your goals.
            </p>
          </Reveal>

          <Reveal variant="slide-left" delay={220}>
            <p className="hero-launch-date">
              Available <strong>now on iPhone and Android</strong>.
            </p>
          </Reveal>

          <Reveal variant="slide-left" delay={280}>
            <div className="hero-bonus-hint">
              <span className="bonus-icon" aria-hidden>
                🎁
              </span>
              <span>
                <strong>Thryvin' AI coaching.</strong> Built around your training.
              </span>
            </div>
          </Reveal>

          <Reveal variant="slide-left" delay={340}>
            <AppStoreButtons />
          </Reveal>
        </div>

        <div className="hero-visual hero-visual--artwork" ref={heroVisualRef}>
          <Reveal variant="slide-right" delay={160} className="hero-artwork">
            <picture>
              <source
                media="(max-width: 960px)"
                srcSet="/images/gym-search-480.webp 480w, /images/gym-search-960.webp 960w"
                sizes="(max-width: 640px) calc(100vw - 32px), 640px"
                width="960"
                height="640"
              />
              <img
                src="/images/gym-search-960.webp"
                srcSet="/images/gym-search-480.webp 480w, /images/gym-search-960.webp 960w"
                sizes="(max-width: 1440px) 44vw, 640px"
                width="960"
                height="640"
                alt="Thryvin' in a purple-lit gym, showing workout planning and exercise guidance on two phones"
                fetchPriority="high"
              />
            </picture>
          </Reveal>
          <div className="hero-orb orb-1" aria-hidden></div>
          <div className="hero-orb orb-2" aria-hidden></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
